import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { SidePanel } from '@hotjar/webapp/features/Playbacks';
import { PlaybackArea } from '@hotjar/webapp/features/Playbacks/PlayerPage/components/PlaybackArea';
import { goToPollCreatePage } from '@hotjar/recordings/src/features/Playbacks/utils';
import { Playlist } from '@hotjar/webapp/features/Playbacks/Playlist';
import { useSelector, useStore, useStoreState } from '@hotjar/recordings/src/store/player/context';
import { goToRecordingFromPlaylist } from '@hotjar/recordings/src/features/Playbacks/utils/goToRecordingFromPlaylist';
import { TabCounter as TabCounterWithMultiTab } from '@hotjar/webapp/features/Playbacks/TopMenu/TabCounter';
import { CopyToClipboard } from '@hotjar/webapp/features/Playbacks/TopMenu/CopyToClipboard';
import { AddressBarIcon } from '@hotjar/webapp/features/Playbacks/TopMenu/AddressBarIcon';
import { useRealTimeStore } from '@hotjar/webapp/features/Playbacks/store/realTimeStore';
import { trackClick } from '@hotjar/webapp/features/Playbacks/utils/tracking';
import type { Recording } from '@hotjar/webapp/features/Playbacks/types';
import { OPENER } from '@hotjar/recordings/src/features/Playbacks/constants/opener';

const pulse = keyframes`
  from {
    opacity: 0.1;
  }
  50% {
    opacity: 1;
  }
  to {
    opacity: 0.1;
  }
`;

const AddressBar = styled.div<{ isLoading: boolean }>`
  height: 32px;
  flex-grow: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0 ${tokens.spacer.x1pt5};

  border-bottom: 1px solid ${tokens.BRAND_COLORS.neutral_300};
  color: ${tokens.color.text_default};
  overflow: hidden;

  background-color: ${tokens.color.neutral_100};

  ${({ isLoading }) =>
    isLoading &&
    css`
      animation: ${pulse} 1.5s linear infinite;
    `}
`;

const UrlContainer = styled.div`
  max-width: 671px;
  flex-shrink: 1;
  flex-grow: 1;

  text-align: left;

  display: inline-block;
  font-size: 12px;

  ${tokens.truncateText()}
`;

const CenterWrapper = styled.div`
  width: 100%;
  min-width: 0;

  flex-grow: 1;
  flex-shrink: 1;

  display: flex;
  flex-direction: column;
`;

export const getHeatmapUrlBy = (siteId: number, url: string): string => {
  const urlParams = new URLSearchParams({
    url,
    match: 'simple_match',
    device: 'desktop',
    type: 'click',
    range: 'relative',
    dates: '12m',
  });

  // URL brings to the corresponding CC heatmap with data for the past 12 months
  return `/sites/${siteId}/heatmap/view?${urlParams.toString()}`;
};

export const splitUrl = (url: string): { domain?: string; path?: string } => {
  if (!url) {
    return {};
  }
  try {
    const urlObject = new URL(url);
    const { protocol, host, pathname, search, hash } = urlObject;
    const domain = `${protocol}//${host}`;
    const path = pathname + search + hash;

    return { domain, path };
  } catch {
    return { domain: url, path: '' };
  }
};

export const CenterSection = (): JSX.Element => {
  const { t } = useTranslation();
  const playerStore = useStore();
  const { fetchNextRecordingsPage } = useStoreState();
  const {
    playlistVisible,
    recordingsList,
    lastFetchedRecordingId,
    playing,
    playlistOpened,
    hasMoreRecordings,
    watchedRecordingsIdsList,
    sortBy,
    recording,
    site,
    sidePanelEventMarkers,
    showSidePanel,
    showPollUpsell,
    currentPageOriginalUrl,
    loaded,
    siteId,
    isSharedRecording,
    playbacksOpener,
    isLightPlayer,
  } = useSelector((state) => ({
    playlistVisible: state.playlistVisible,
    recordingsList: state.recordingsList,
    lastFetchedRecordingId: state.lastFetchedRecordingId,
    playing: state.playing,
    playlistOpened: state.playlistOpened,
    hasMoreRecordings: state.hasMoreRecordings,
    watchedRecordingsIdsList: state.watchedRecordingsIdsList,
    sortBy: state.sortBy,
    recording: state.recording,
    site: state.site,
    sidePanelEventMarkers: state.sidePanelEventMarkers,
    showSidePanel: state.showSidePanel,
    showPollUpsell: state.showPollUpsell,
    currentPageOriginalUrl: state.currentPageOriginalUrl,
    loaded: state.loaded,
    siteId: state.siteId,
    isSharedRecording: state.isSharedRecording,
    playbacksOpener: state.playbacksOpener,
    isLightPlayer: state.isLightPlayer,
  }));

  const goToRecording = useCallback(
    (id: number) => {
      goToRecordingFromPlaylist(playerStore, id);
    },
    [playerStore],
  );

  const isLoadingOrNoRecording = !loaded || !recording;
  const { domain, path } = splitUrl(currentPageOriginalUrl || '');
  const heatmapUrl = getHeatmapUrlBy(siteId, currentPageOriginalUrl || '');

  const { tabCount } = useRealTimeStore.getState();
  const currentTabIndex = useRealTimeStore((state) => state.currentTabIndex);
  const [syntheticTabIndex, setSyntheticTabIndex] = useState(1);

  const onUrlBarButtonClick = useCallback(
    (elementName) =>
      trackClick(recording as Recording, {
        element_name: elementName,
        state_event_was_triggered_on: 'playback.player_header',
        ui_position: 'clicked-icon',
      }),
    [recording],
  );

  useEffect(() => {
    if (currentTabIndex === undefined) {
      return;
    }

    setSyntheticTabIndex(currentTabIndex + 1);
  }, [currentTabIndex]);

  return (
    <main className="center-section">
      {playlistVisible && !isLightPlayer ? (
        <Playlist
          recordings={recordingsList}
          activeRecrodingId={lastFetchedRecordingId}
          isPlaying={playing}
          goToRecording={goToRecording}
          fetchNextRecordingsPage={fetchNextRecordingsPage}
          playlistOpened={playlistOpened}
          hasMoreRecordings={hasMoreRecordings}
          watchedRecordingsIdsList={watchedRecordingsIdsList}
          sortBy={sortBy}
          isPlayingFunnels={playbacksOpener === OPENER.FUNNELS}
        />
      ) : null}

      <CenterWrapper>
        <AddressBar id="address-bar" isLoading={isLoadingOrNoRecording}>
          <TabCounterWithMultiTab currentTabNo={syntheticTabIndex} totalTabNo={tabCount} />
          <UrlContainer data-testid="recording-url-container">
            {domain ? (
              <span
                css={css`
                  opacity: 0.6;
                `}
              >
                {domain}
              </span>
            ) : (
              <span>&nbsp;</span>
            )}
            <span
              css={css`
                font-weight: 500;
                opacity: ${path?.length === 1 ? 0.6 : 1};
              `}
            >
              {path}
            </span>
          </UrlContainer>
          <CopyToClipboard
            onCopy={() => onUrlBarButtonClick('copy-icon')}
            toCopy={currentPageOriginalUrl || ''}
            placement="bottom"
          />
          <a
            css={css`
              margin-left: ${tokens.spacer.x2};
            `}
            href={currentPageOriginalUrl || ''}
            rel="noopener noreferrer"
            target="_blank"
            onClick={() => onUrlBarButtonClick('open_in_new_tab-icon')}
          >
            <AddressBarIcon
              tooltip={t('common:player.url-bar.open-in-new-tab')}
              icon="open_new_window"
              data-testid="open-new-window-icon"
            />
          </a>
          {!isSharedRecording && (
            <a
              css={css`
                margin-left: ${tokens.spacer.x2};
              `}
              href={heatmapUrl}
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => onUrlBarButtonClick('generate_heatmap-icon')}
            >
              <AddressBarIcon
                tooltip={t('common:player.url-bar.view-heatmap')}
                icon="heatmaps"
                data-testid="heatmap-icon"
              />
            </a>
          )}
        </AddressBar>

        <PlaybackArea />
      </CenterWrapper>

      {recording && !isLightPlayer ? (
        <SidePanel
          site={site}
          onCreatePollClick={() => goToPollCreatePage(playerStore)}
          recording={recording}
          sidePanelEventMarkers={sidePanelEventMarkers}
          showSidePanel={showSidePanel}
          showPollUpsell={showPollUpsell}
        />
      ) : null}
    </main>
  );
};
